import type {
  Folio,
  GroupBillingContribution,
  GroupBillingMode,
  GroupBillingSummary,
  HosState,
  Reservation,
} from "./hosTypes";

type GroupBillingAccountResult = {
  folios: Folio[];
  summary: GroupBillingSummary;
};

const ROOM_RATE_BY_TYPE: Record<string, number> = {
  "rt-std": 120,
  "rt-dlx": 165,
  "rt-ste": 240,
};

function nightsBetween(arrival: string, departure: string) {
  const start = new Date(`${arrival}T00:00:00`);
  const end = new Date(`${departure}T00:00:00`);
  const diff = Math.max(0, end.getTime() - start.getTime());
  return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)));
}

function createFolioId(prefix: string) {
  return `${prefix}-${Math.floor(1000 + Math.random() * 8999)}`;
}

function buildContribution(
  room: NonNullable<Reservation["groupedRooms"]>[number],
  existingFolio?: Folio
): GroupBillingContribution {
  const nightly = ROOM_RATE_BY_TYPE[room.roomTypeId] ?? 120;
  return {
    id: room.id,
    roomId: room.id,
    roomTypeId: room.roomTypeId,
    amount: nightly * nightsBetween(room.arrival, room.departure),
    folioId: existingFolio?.id,
    guestLabel: room.guest ? `${room.guest.firstName ?? ""} ${room.guest.lastName ?? ""}`.trim() || "Unassigned" : "Unassigned",
  };
}

export function createGroupBillingAccount(
  groupReservation: Reservation,
  mode: GroupBillingMode
): GroupBillingAccountResult {
  const groupRooms = groupReservation.groupedRooms ?? [];
  const contributions = groupRooms.map((room) => buildContribution(room));

  if (mode === "shared") {
    const totalBalance = contributions.reduce((sum, item) => sum + item.amount, 0);
    const masterFolio: Folio = {
      id: createFolioId("GF"),
      reservationId: groupReservation.id,
      groupReservationId: groupReservation.id,
      guestId: groupReservation.primaryGuestId ?? groupReservation.guestId,
      folioType: "group_master",
      currency: "EUR",
      balance: totalBalance,
      totalCharges: totalBalance,
      totalPayments: 0,
      totalRefunds: 0,
      status: "open",
    };
    return {
      folios: [masterFolio],
      summary: {
        groupReservationId: groupReservation.id,
        billingMode: mode,
        masterFolioId: masterFolio.id,
        totalBalance,
        folioIds: [masterFolio.id],
        contributions: contributions.map((item) => ({ ...item, folioId: masterFolio.id })),
      },
    };
  }

  const folios = contributions.map((item, index) => ({
    id: createFolioId("GF"),
    reservationId: groupReservation.id,
    groupReservationId: groupReservation.id,
    groupRoomId: groupRooms[index]?.id,
    guestId: groupRooms[index]?.guestId ?? groupReservation.primaryGuestId ?? groupReservation.guestId,
    folioType: "group_room" as const,
    currency: "EUR" as const,
    balance: item.amount,
    totalCharges: item.amount,
    totalPayments: 0,
    totalRefunds: 0,
    status: "open" as const,
  }));

  return {
    folios,
    summary: {
      groupReservationId: groupReservation.id,
      billingMode: mode,
      totalBalance: folios.reduce((sum, folio) => sum + folio.balance, 0),
      folioIds: folios.map((folio) => folio.id),
      contributions: contributions.map((item, index) => ({ ...item, folioId: folios[index]?.id })),
    },
  };
}

export function getGroupBillingSummary(
  groupReservationId: string,
  state: Pick<HosState, "reservations" | "folios">
): GroupBillingSummary | null {
  const reservation = state.reservations.find((entry) => entry.id === groupReservationId && entry.reservationType === "group");
  if (!reservation) return null;
  const groupFolios = state.folios.filter((folio) => folio.groupReservationId === groupReservationId);
  const contributions = (reservation.groupedRooms ?? []).map((room) => {
    const folio = groupFolios.find((entry) => entry.groupRoomId === room.id) ?? groupFolios[0];
    return buildContribution(room, folio);
  });

  return {
    groupReservationId,
    billingMode: reservation.billingMode ?? "individual",
    masterFolioId: groupFolios.find((folio) => folio.folioType === "group_master")?.id,
    totalBalance: groupFolios.reduce((sum, folio) => sum + folio.balance, 0),
    folioIds: groupFolios.map((folio) => folio.id),
    contributions,
  };
}

export function postChargeToGroupOrRoom(
  folios: Folio[],
  groupReservationId: string,
  amount: number,
  roomId?: string
) {
  return folios.map((folio) => {
    if (folio.groupReservationId !== groupReservationId) return folio;
    if (roomId && folio.groupRoomId !== roomId) return folio;
    if (!roomId && folio.folioType !== "group_master") return folio;
    return { ...folio, balance: folio.balance + amount };
  });
}
